import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Tabs,
  Table,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Tag,
  Image,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
  Statistic,
  Progress,
  Tooltip,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import logo from "../../assets/Logo_Prosperidad_Social.png";

import { getAutorizados, getSoportesLote } from "../../services/reporteRit/autorizadosEntregasService";
import cargarDeptosMunicipios from "../../services/departMunic/deptosMunicipiosServices";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Mismos colores por número de entrega en toda la app (Informe General de
// Entregas usa este mismo esquema: azul / morado / magenta).
const ENCUESTAS = [
  { key: "573", label: "Entregas 1", color: "#1677ff" },
  { key: "667", label: "Entregas 2", color: "#722ed1" },
  { key: "690", label: "Entregas 3", color: "#eb2f96" },
];

const PAGE_SIZE_DEFAULT = 20;

const FILTROS_VACIOS = { cedula: "", fechaInicio: null, fechaFin: null, municipioId: undefined };

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE GRUPOS PARA EL EXCEL — mismo estilo que Informe General de
// Entregas (encabezados agrupados a color + logo + resumen por ubicación).
// Solo se incluyen campos que existen y significan algo en este reporte:
// "Quién reclama" no aplica (siempre es "Autorizado" acá, por definición del
// filtro), y no hay un "estado aprobado/rechazado" en este listado.
// ═══════════════════════════════════════════════════════════════════════════

const GRUPOS_EXPORT = [
  {
    titulo: "Información General",
    colorGrupo: "FF1B5E20",
    colorHeader: "FF2E7D32",
    columnas: [
      { label: "Cédula", campo: (r) => r.identificacion },
      { label: "Municipio", campo: (r) => r.municipio || "—" },
      { label: "Departamento", campo: (r) => r.departamento || "—" },
      {
        label: "Fecha diligenciamiento",
        campo: (r) => (r.fechaDiligenciamiento ? dayjs(r.fechaDiligenciamiento).format("YYYY-MM-DD HH:mm") : ""),
      },
    ],
  },
  {
    titulo: "Titular",
    colorGrupo: "FF0D47A1",
    colorHeader: "FF1565C0",
    columnas: [{ label: "Nombre titular", campo: (r) => r.nombreCompleto || "—" }],
  },
  {
    titulo: "Autorizado",
    colorGrupo: "FFE65100",
    colorHeader: "FFEF6C00",
    columnas: [{ label: "Nombre autorizado", campo: (r) => r.nombreCompletoAut || "—" }],
  },
  {
    titulo: "Soportes",
    colorGrupo: "FF4A148C",
    colorHeader: "FF6A1B9A",
    columnas: [{ label: "N° imágenes", campo: (r) => `${r.imagenesEsperadas}/4` }],
  },
];

const TOTAL_COLUMNAS_EXPORT = GRUPOS_EXPORT.reduce((s, g) => s + g.columnas.length, 0);

// ── Miniatura de un soporte: mientras se resuelve muestra Spin; si no existe
// (o falló), no muestra nada — nunca un ícono roto ni un placeholder vacío ──
function SoporteThumb({ archivo, cargando, label }) {
  if (cargando) {
    return (
      <div style={{ width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (!archivo || archivo.estado !== "ok" || !archivo.base64) return null;

  return (
    <Image
      src={archivo.base64}
      alt={label}
      width={56}
      height={56}
      style={{ objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
      preview={{ mask: false }}
    />
  );
}

export default function AutorizadosEntregas() {
  const token = useSelector((state) => state.auth.user?.token);

  const [idEncuesta, setIdEncuesta] = useState("573");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const [filtrosDraft, setFiltrosDraft] = useState(FILTROS_VACIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VACIOS);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [progresoExport, setProgresoExport] = useState(0);

  const [soportesPorCedula, setSoportesPorCedula] = useState({});
  const [cedulasCargando, setCedulasCargando] = useState(new Set());

  const [municipios, setMunicipios] = useState([]);

  // Evita que una respuesta de red atrasada (de una página/pestaña/filtro que
  // el usuario ya abandonó) pise los datos de lo que se está viendo ahora.
  const peticionVigenteRef = useRef(0);

  const entregaActual = ENCUESTAS.find((e) => e.key === idEncuesta);

  // ── Municipios para el filtro (se cargan una sola vez) ──
  useEffect(() => {
    (async () => {
      const resp = await cargarDeptosMunicipios();
      if (resp?.data) {
        const plano = resp.data.flatMap((depto) =>
          (depto.municipios || []).map((m) => ({
            value: m.oid_municipio,
            label: m.nombre_municipio,
          }))
        );
        plano.sort((a, b) => a.label.localeCompare(b.label));
        setMunicipios(plano);
      }
    })();
  }, []);

  const construirParams = useCallback(
    (overrides = {}) => ({
      idEncuesta: Number(idEncuesta),
      page,
      pageSize,
      cedula: filtrosAplicados.cedula || undefined,
      fechaInicio: filtrosAplicados.fechaInicio || undefined,
      fechaFin: filtrosAplicados.fechaFin || undefined,
      municipioId: filtrosAplicados.municipioId || undefined,
      ...overrides,
    }),
    [idEncuesta, page, pageSize, filtrosAplicados]
  );

  // ── Cargar la página actual (rápido: solo BD, sin tocar la API externa) ──
  const cargarPagina = useCallback(async () => {
    const miPeticion = ++peticionVigenteRef.current;
    setLoading(true);
    try {
      const resp = await getAutorizados(construirParams(), token);
      if (miPeticion !== peticionVigenteRef.current) return; // respuesta obsoleta
      setRows(resp.rows || []);
      setTotal(resp.total || 0);
    } catch (e) {
      console.error(e);
      if (miPeticion === peticionVigenteRef.current) message.error("Error al cargar los registros.");
    } finally {
      if (miPeticion === peticionVigenteRef.current) setLoading(false);
    }
  }, [construirParams, token]);

  useEffect(() => {
    cargarPagina();
  }, [cargarPagina]);

  // ── Traer los soportes SOLO de las cédulas de la página visible, en un
  // único lote (el backend limita la concurrencia contra la API externa) ──
  useEffect(() => {
    if (rows.length === 0) return;
    const miPeticion = peticionVigenteRef.current;
    const identificaciones = rows.map((r) => r.identificacion);

    setCedulasCargando(new Set(identificaciones));

    (async () => {
      try {
        const soportes = await getSoportesLote(Number(idEncuesta), identificaciones, token);
        if (miPeticion !== peticionVigenteRef.current) return; // ya se cambió de página/pestaña/filtro
        setSoportesPorCedula((prev) => ({ ...prev, ...soportes }));
      } catch (e) {
        console.error(e);
      } finally {
        if (miPeticion === peticionVigenteRef.current) setCedulasCargando(new Set());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, idEncuesta, token]);

  // ── Cambiar de pestaña de entrega ──
  const handleCambiarEncuesta = (nuevaKey) => {
    setIdEncuesta(nuevaKey);
    setPage(1);
  };

  // ── Filtros ──
  const handleBuscar = () => {
    setFiltrosAplicados(filtrosDraft);
    setPage(1);
  };

  const handleLimpiarFiltros = () => {
    setFiltrosDraft(FILTROS_VACIOS);
    setFiltrosAplicados(FILTROS_VACIOS);
    setPage(1);
  };

  const hayFiltrosActivos =
    !!filtrosAplicados.cedula ||
    !!filtrosAplicados.fechaInicio ||
    !!filtrosAplicados.municipioId;

  // ── Excel: helpers de estilo (mismo patrón que Informe General de Entregas) ──

  const numToColLetter = (n) => {
    let result = "";
    while (n > 0) {
      const rem = (n - 1) % 26;
      result = String.fromCharCode(65 + rem) + result;
      n = Math.floor((n - 1) / 26);
    }
    return result || "A";
  };

  const aplicarEncabezadoSheet = (worksheet, titulo, totalColumnas, logoImageId) => {
    const colFin = numToColLetter(Math.max(totalColumnas, 4));
    if (logoImageId !== null) {
      worksheet.mergeCells("A1:B3");
      worksheet.addImage(logoImageId, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    }
    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell = worksheet.getCell("C1");
    titleCell.value = titulo;
    titleCell.font = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell = worksheet.getCell("C3");
    dateCell.value = `Fecha de exportación: ${new Date().toLocaleDateString("es-CO")}`;
    dateCell.alignment = { horizontal: "center" };
    worksheet.addRow([]);
  };

  const bordeCompleto = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };

  // ── Resumen por ubicación al final de la hoja ──
  const agregarResumenPorUbicacion = (worksheet, filas) => {
    const mapa = new Map();
    filas.forEach((r) => {
      const departamento = r.departamento || "—";
      const municipio = r.municipio || "—";
      const clave = `${departamento}|||${municipio}`;
      if (!mapa.has(clave)) mapa.set(clave, { departamento, municipio, total: 0, completos: 0 });
      const registro = mapa.get(clave);
      registro.total++;
      if (Number(r.imagenesEsperadas) >= 4) registro.completos++;
    });

    const filasResumen = [...mapa.values()].sort(
      (a, b) => a.departamento.localeCompare(b.departamento) || a.municipio.localeCompare(b.municipio)
    );

    worksheet.addRow([]);
    worksheet.addRow([]);

    const tituloRowNum = worksheet.rowCount + 1;
    worksheet.mergeCells(tituloRowNum, 1, tituloRowNum, 5);
    const tituloCell = worksheet.getCell(tituloRowNum, 1);
    tituloCell.value = "RESUMEN POR UBICACIÓN";
    tituloCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    tituloCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
    tituloCell.alignment = { horizontal: "center", vertical: "middle" };

    const headerRow = worksheet.addRow(["Departamento", "Municipio", "Total", "Completos (4/4)", "Incompletos"]);
    const coloresHeader = ["FF1B5E20", "FF1B5E20", "FF4A148C", "FF2E7D32", "FFC62828"];
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: coloresHeader[colNumber - 1] } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = bordeCompleto;
    });

    let totalGeneral = 0;
    let totalCompletos = 0;

    filasResumen.forEach((r) => {
      const incompletos = r.total - r.completos;
      totalGeneral += r.total;
      totalCompletos += r.completos;

      const row = worksheet.addRow([r.departamento, r.municipio, r.total, r.completos, incompletos]);
      row.eachCell((cell, colNumber) => {
        cell.border = bordeCompleto;
        cell.alignment = colNumber >= 3 ? { horizontal: "center" } : { horizontal: "left" };
      });
    });

    const totalIncompletos = totalGeneral - totalCompletos;
    const totalRow = worksheet.addRow(["TOTAL GENERAL", "", totalGeneral, totalCompletos, totalIncompletos]);
    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 2);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { ...bordeCompleto, bottom: { style: "double" } };
    });

    const anchoMinDepto = Math.max(18, ...filasResumen.map((r) => r.departamento.length + 3));
    const anchoMinMunic = Math.max(18, ...filasResumen.map((r) => r.municipio.length + 3));
    [anchoMinDepto, anchoMinMunic, 12, 16, 14].forEach((ancho, i) => {
      const col = worksheet.getColumn(i + 1);
      col.width = Math.max(col.width || 0, ancho);
    });
  };

  // ── Hoja principal con encabezados agrupados a color ──
  const crearHojaAutorizados = (workbook, filas, entrega, logoImageId) => {
    const worksheet = workbook.addWorksheet(`Autorizados ${entrega.label}`);

    aplicarEncabezadoSheet(worksheet, `SOPORTES AUTORIZADOS — ${entrega.label.toUpperCase()}`, TOTAL_COLUMNAS_EXPORT, logoImageId);

    // ── Fila de grupos ──
    const groupRow = worksheet.rowCount + 1;
    let colCursor = 1;
    GRUPOS_EXPORT.forEach((grupo) => {
      const inicio = colCursor;
      const fin = colCursor + grupo.columnas.length - 1;
      worksheet.mergeCells(groupRow, inicio, groupRow, fin);
      const cell = worksheet.getCell(groupRow, inicio);
      cell.value = grupo.titulo.toUpperCase();
      cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorGrupo } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      for (let c = inicio; c <= fin; c++) {
        worksheet.getCell(groupRow, c).border = bordeCompleto;
      }
      colCursor = fin + 1;
    });
    worksheet.getRow(groupRow).height = 22;

    // ── Fila de encabezados de columna ──
    const headerRow = groupRow + 1;
    colCursor = 1;
    GRUPOS_EXPORT.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const cell = worksheet.getCell(headerRow, colCursor);
        cell.value = col.label;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorHeader } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = bordeCompleto;
        colCursor++;
      });
    });
    worksheet.getRow(headerRow).height = 28;

    // ── Filas de datos ──
    filas.forEach((fila) => {
      const valores = [];
      GRUPOS_EXPORT.forEach((grupo) => grupo.columnas.forEach((col) => valores.push(col.campo(fila))));
      const row = worksheet.addRow(valores);
      row.eachCell((cell) => {
        cell.border = bordeCompleto;
        cell.alignment = { vertical: "middle" };
      });
    });

    // ── Anchos de columna ──
    colCursor = 1;
    GRUPOS_EXPORT.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const valores = filas.map((f) => String(col.campo(f) ?? ""));
        const ancho = Math.max(col.label.length, ...valores.map((v) => v.length), 10) + 3;
        worksheet.getColumn(colCursor).width = ancho;
        colCursor++;
      });
    });

    worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: headerRow, showGridLines: true }];

    agregarResumenPorUbicacion(worksheet, filas);

    return worksheet;
  };

  // ── Exportar (solo datos, sin imágenes — todas las filas que cumplan el filtro) ──
  const handleExportar = async () => {
    setExportando(true);
    setProgresoExport(10);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema Bodecom";
      workbook.created = new Date();

      let logoImageId = null;
      try {
        const response = await fetch(logo);
        const buffer = await response.arrayBuffer();
        logoImageId = workbook.addImage({ buffer, extension: "png" });
      } catch {
        console.warn("Logo no disponible, se omitirá en el Excel");
      }

      setProgresoExport(30);

      const resp = await getAutorizados(construirParams({ exportAll: true, page: 1, pageSize: 0 }), token);
      const data = resp.rows || [];

      if (data.length === 0) {
        message.warning("No hay registros para exportar con los filtros actuales.");
        return;
      }

      setProgresoExport(70);

      crearHojaAutorizados(workbook, data, entregaActual, logoImageId);

      setProgresoExport(95);

      const periodoLabel = filtrosAplicados.fechaInicio && filtrosAplicados.fechaFin
        ? `_${filtrosAplicados.fechaInicio}_${filtrosAplicados.fechaFin}`
        : "";
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `SoportesAutorizados_${entregaActual.label.replace(" ", "")}${periodoLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      message.success(`Excel exportado con ${data.length.toLocaleString("es-CO")} registros.`);
    } catch (e) {
      console.error(e);
      message.error("Error al exportar.");
    } finally {
      setExportando(false);
      setProgresoExport(0);
    }
  };

  const columnasSoporte = [
    { titulo: "Foto beneficio", campo: "fotoBeneficio" },
    { titulo: "Doc. frontal", campo: "docFrontal" },
    { titulo: "Foto autorización", campo: "fotoAutorizacion" },
    { titulo: "Firma", campo: "firma" },
  ];

  const columns = [
    { title: "Cédula", dataIndex: "identificacion", key: "identificacion", width: 130, fixed: "left" },
    { title: "Nombre titular", dataIndex: "nombreCompleto", key: "nombreCompleto", width: 200, render: (v) => v || "—" },
    { title: "Nombre autorizado", dataIndex: "nombreCompletoAut", key: "nombreCompletoAut", width: 200, render: (v) => v || "—" },
    { title: "Municipio", dataIndex: "municipio", key: "municipio", width: 140 },
    {
      title: "Fecha",
      dataIndex: "fechaDiligenciamiento",
      key: "fechaDiligenciamiento",
      width: 130,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "N° imágenes",
      dataIndex: "imagenesEsperadas",
      key: "imagenesEsperadas",
      width: 110,
      align: "center",
      render: (v) => <Tag color={v >= 4 ? "green" : v > 0 ? "orange" : "red"}>{v}/4</Tag>,
    },
    ...columnasSoporte.map(({ titulo, campo }) => ({
      title: titulo,
      key: campo,
      width: 90,
      align: "center",
      render: (_, row) => (
        <Image.PreviewGroup>
          <SoporteThumb
            archivo={soportesPorCedula[row.identificacion]?.[campo]}
            cargando={cedulasCargando.has(row.identificacion)}
            label={`${titulo} — ${row.identificacion}`}
          />
        </Image.PreviewGroup>
      ),
    })),
  ];

  return (
    <Card style={{ margin: 20 }}>
      <Typography.Title level={3}>Soportes de Entregas Autorizadas</Typography.Title>

      <Tabs
        activeKey={idEncuesta}
        onChange={handleCambiarEncuesta}
        items={ENCUESTAS.map((e) => ({ key: e.key, label: e.label }))}
      />

      {/* ── Tarjeta de total (mismo lenguaje visual que Informe General de Entregas) ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: `4px solid ${entregaActual.color}` }}>
            <Statistic
              title={`Total ${entregaActual.label} (Autorizado)`}
              value={total}
              valueStyle={{ color: entregaActual.color }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Acciones ── */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Tooltip title={exportando ? "Generando Excel..." : `Exportar ${total.toLocaleString("es-CO")} registros`}>
          <Button
            icon={exportando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={handleExportar}
            loading={exportando}
            disabled={exportando || total === 0}
            style={{ borderColor: "#217346", color: exportando ? undefined : "#217346" }}
          >
            {exportando ? `Descargando ${progresoExport}%` : "Exportar Excel"}
          </Button>
        </Tooltip>
        <Button icon={<ReloadOutlined />} onClick={cargarPagina} disabled={loading || exportando}>
          Actualizar
        </Button>
      </Space>

      {/* ── Progreso de exportación ── */}
      {exportando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress percent={progresoExport} status="active" strokeColor={{ "0%": "#217346", "100%": "#52c41a" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel, no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Filtros ── */}
      <Divider orientation="left" style={{ margin: "12px 0" }}>
        <Space>
          <FilterOutlined /> Filtrar por cédula, período o municipio
        </Space>
      </Divider>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} sm={12} md={6} lg={5}>
          <Input
            placeholder="Buscar por cédula"
            value={filtrosDraft.cedula}
            onChange={(e) => setFiltrosDraft((f) => ({ ...f, cedula: e.target.value.replace(/\D/g, "") }))}
            onPressEnter={handleBuscar}
            allowClear
          />
        </Col>
        <Col xs={24} sm={14} md={10} lg={8}>
          <RangePicker
            style={{ width: "100%" }}
            value={
              filtrosDraft.fechaInicio && filtrosDraft.fechaFin
                ? [dayjs(filtrosDraft.fechaInicio), dayjs(filtrosDraft.fechaFin)]
                : null
            }
            format="YYYY-MM-DD"
            placeholder={["Fecha inicio", "Fecha fin"]}
            onChange={(valores) =>
              setFiltrosDraft((f) => ({
                ...f,
                fechaInicio: valores?.[0] ? valores[0].format("YYYY-MM-DD") : null,
                fechaFin: valores?.[1] ? valores[1].format("YYYY-MM-DD") : null,
              }))
            }
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Municipio"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            options={municipios}
            value={filtrosDraft.municipioId}
            onChange={(v) => setFiltrosDraft((f) => ({ ...f, municipioId: v }))}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
            Buscar
          </Button>
        </Col>
        {hayFiltrosActivos && (
          <Col>
            <Button icon={<ClearOutlined />} onClick={handleLimpiarFiltros}>
              Limpiar
            </Button>
          </Col>
        )}
      </Row>

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Text type="secondary">
            Mostrando <Text strong>{total.toLocaleString("es-CO")}</Text> registro{total !== 1 ? "s" : ""} con
            estado <Text strong>"Autorizado"</Text>
            {hayFiltrosActivos && <Text type="warning"> (filtro activo)</Text>}
          </Text>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="identificacion"
        loading={loading}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (t, range) => `${range[0]}–${range[1]} de ${t.toLocaleString("es-CO")} registros`,
          onChange: (nuevaPagina, nuevoPageSize) => {
            setPage(nuevaPagina);
            if (nuevoPageSize !== pageSize) setPageSize(nuevoPageSize);
          },
        }}
      />
    </Card>
  );
}
